<template>
  <section class="women-banner spad" ref="section">
    <!-- Search + Sales Type Filter -->
    <div class="filter-bar my-4 row align-items-center mx-1 mx-md-0">
      <div class="col-12 px-1 d-flex justify-content-center justify-content-md-start flex-wrap gap-2">
        <!-- "All" button -->
        <button type="button" class="btn-tab" :class="{ active: selectedSalesType === 'all' }"
          @click="handleSalesTypeClick('all')">
          {{ $t('all_sales_types') }}
        </button>

        <!-- Buttons from salesTypes -->
        <button v-for="type in salesTypes" :key="type" type="button" class="btn-tab"
          :class="{ active: selectedSalesType === type }" @click="handleSalesTypeClick(type)">
          {{ $t(`ecommerce.${type}`) }}
        </button>
      </div>
    </div>

    <!-- Results Count -->
    <div class="results-info mb-3">
      <p v-if="filteredProducts.length > 0">
        {{ $t('found_products', { count: filteredProducts.length }) }}
      </p>
      <p v-else class="no-results">{{ $t('no_results') }}</p>
    </div>

    <!-- Products Grid -->
    <div class="row row-min-size g-3 products-grid mx-1 mx-md-0" ref="productsGrid">
      <div class="col-6 col-xs-10 offset-xs-1 col-md-3 col-lg-3 col-xl-3 d-flex align-items-stretch px-1 px-md-2 mb-2"
        v-for="product in visibleProducts" :key="product.id">
        <ProductSingle 
          class="product-item w-100" 
          :showDescription="false"
          :product="product"
          @product-clicked="handleProductClick"
        />
      </div>
    </div>

    <!-- Show More Button -->
    <div class="d-flex justify-content-center mt-4" v-if="filteredProducts.length > productsToShow">
      <button class="btn btn-primary" @click="showMoreProducts">{{ $t('show_more') }}</button>
    </div>
  </section>
</template>

<script>
import ProductSingle from "@/components/templates/ProductSingle";
import gsapAnimations from "@/services/gsapAnimations";
import cookieUtils from "@/services/cookieUtils";

export default {
  props: {
    randomProducts: Array,
    searchQuery: {
      type: String,
      default: "",
    },
  },
  components: { ProductSingle },
  data() {
    return {
      selectedSalesType: "all",
      productsToShow: 8,
    };
  },
  computed: {
    salesTypes() {
      const types = this.randomProducts
        .map((p) => p.sales_type)
        .filter((t) => t && t.trim() !== "");
      return [...new Set(types)];
    },
    filteredProducts() {
      const query = (this.searchQuery || '').trim().toLowerCase();

      return this.randomProducts.filter((product) => {
        const matchesSearch =
          !query ||
          product.name?.toLowerCase().includes(query) ||
          product.fixed_price?.toLowerCase().includes(query) ||
          product.description?.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query);

        const matchesSalesType =
          this.selectedSalesType === "all" ||
          product.sales_type === this.selectedSalesType;

        return matchesSearch && matchesSalesType;
      });
    },
    visibleProducts() {
      return this.filteredProducts.slice(0, this.productsToShow);
    },
  },
  watch: {
    filteredProducts() {
      this.$nextTick(() => {
        gsapAnimations.setupScrollAnimation(
          this.$refs.section,
          ".product-item"
        );
      });
    },
  },
  methods: {
    handleProductClick(product) {
      console.log('Product clicked:', product);
      
      // Track category preference when user clicks on a product
      if (product.category_id) {
        console.log('Tracking category:', product.category_id);
        cookieUtils.trackCategoryPreference(product.category_id);
      }
      
      // Track sales type preference
      if (product.sales_type) {
        console.log('Tracking sales type:', product.sales_type);
        cookieUtils.trackSalesTypePreference(product.sales_type);
      }

      // Verify cookies were set
      console.log('Category preferences:', cookieUtils.getCookie('user_category_preferences'));
      console.log('Sales type preferences:', cookieUtils.getCookie('user_salestype_preferences'));
    },
    
    handleSalesTypeClick(type) {
      this.selectedSalesType = type;
      
      // Track sales type filter selection
      if (type !== 'all') {
        cookieUtils.trackSalesTypePreference(type);
        console.log('Sales type filter tracked:', type);
      }
    },
    
    showMoreProducts() {
      const oldCount = this.productsToShow;
      this.productsToShow += 8;

      this.$nextTick(() => {
        gsapAnimations.animateItemRange(
          this.$refs.productsGrid,
          ".product-item",
          oldCount,
          this.productsToShow
        );
      });
    },
  },
  mounted() {
    gsapAnimations.setupScrollAnimation(
      this.$refs.section,
      ".product-item"
    );
    
    // Load existing preferences on mount
    // console.log('Existing category preferences:', cookieUtils.getCookie('user_category_preferences'));
    // console.log('Existing sales type preferences:', cookieUtils.getCookie('user_salestype_preferences'));
  },
};
</script>

<style scoped>
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

.sales-type-select {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  font-size: 14px;
  color: var(--color-3);
  background-color: white;
  transition: all 0.25s ease;
  appearance: none;
  cursor: pointer;
}

.sales-type-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: none;
}

.sales-type-select::after {
  content: "";
}

.sales-type-select:hover {
  border-color: var(--color-8);
  background-color: #f9f9f9;
}

.sales-type-select:focus {
  outline: none;
  border-color: var(--color-nz);
  box-shadow: 0 0 6px rgba(0, 123, 255, 0.3);
}

.sales-type-select option {
  background-color: white;
  color: var(--color-3);
  padding: 10px;
  font-size: 14px;
  border: none;
}

.sales-type-select option:hover,
.sales-type-select option:focus {
  background-color: var(--light);
  color: var(--color-nz);
}

.sales-type-select::-ms-expand {
  display: none;
}

.sales-type-select {
  background-color: #fafafa;
  font-weight: 500;
}

.sales-type-select:active {
  background-color: var(--light);
}

.search-input {
  background-color: white;
}

.search-input:hover {
  border-color: var(--color-8);
}

.search-input:focus {
  border-color: var(--color-nz);
  box-shadow: 0 0 6px rgba(0, 123, 255, 0.3);
}

.search-input::placeholder {
  color: var(--color-a);
}

@media (max-width: 576px) {
  .sales-type-select {
    font-size: 15px;
  }
}

@media (max-width: 576px) {
  .btn-tab {
    flex: 1 1 30%;
    max-width: none;
  }
}
</style>